import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import {
  useCancelVerifyDisbursementAccountMutation,
  useGetAccountForVerificationQuery,
  useVerifyDisbursementAccountMutation
} from '../../../api/disbursements/disbursementApi'
import { type VerifyDisbursementAccountPayload } from '../../../api/schema-dtos/AssetHolderDisbursementPayloadDtos'
import { RtkQueryHelper } from '../../../api/shared/RtkQueryHelper'
import { APP_URLS } from '../../../App'
import { useAppDispatch } from '../../../app/hooks'
import { useAuth } from '../../auth/provider/AuthProvider'
import ErrorComponent from '../../shared/components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../shared/components/ui-widgets/LoadingComponent'
import { setNotification } from '../../shared/components/ui-widgets/uiWidgetsSlice'
import AccountsComponent from '../components/finance/AccountsComponent'

const VerifyDisbursementAccountPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { assetHolderId } = useAuth()
  const { token } = useParams()
  const payload: VerifyDisbursementAccountPayload = { assetHolderId, token: token ?? '' }

  const apiGetResult = useGetAccountForVerificationQuery(payload)
  const [apiVerifyTrigger, apiVerifyResult] = useVerifyDisbursementAccountMutation()
  const [apiCancelVerifyTrigger, apiCancelVerifyResult] = useCancelVerifyDisbursementAccountMutation()

  const handleVerifyDisbursementAccount = async (): Promise<void> => {
    apiVerifyTrigger(payload)
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: 'Your cash out account has been successfully verified and can now be used.' }))
        navigate(APP_URLS.dashboard)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        navigate(APP_URLS.dashboard)
      })
  }

  const handleCancelVerifyDisbursementAccount = async (): Promise<void> => {
    apiCancelVerifyTrigger(payload)
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: 'Your verification of the your Cash Out details has been cancelled. Please supply new details.' }))
        navigate(APP_URLS.dashboard)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        navigate(APP_URLS.dashboard)
      })
  }

  return (
    <>
      <Box sx={{ mb: 5, width: '100%' }}>
        <Box>
          <Box sx={{ display: 'flex' }} justifyContent={'space-between'} flexDirection={'row'} alignItems={'center'}>
            <Typography variant={'h3'}>Verify Cash Out Account</Typography>
            <NavLink to={APP_URLS.dashboard}>
              <Button variant={'outlined'} type="button" sx={{ ml: 2 }}>Back to Dashboard</Button>
            </NavLink>
          </Box>

          <Box sx={{ my: 3 }}>
            {apiGetResult.isLoading && <LoadingComponent />}
            {apiGetResult.isError &&
              <ErrorComponent message={RtkQueryHelper.mapResponseError(apiGetResult.error)}/>}
          </Box>

          {apiGetResult.isSuccess && (
            <Box sx={{ my: 3 }}>
              <AccountsComponent disbursementAccounts={[apiGetResult.data]}
                                 hasPassedKyc={true}
                                 maxCardWidth={650}
                                 assetHolderId={assetHolderId}
                                 multiAccountView={false} />

              <Box sx={{ my: 3 }} width={650}>
                <Typography variant={'body1'} sx={{ mb: 3 }}>
                  Please carefully check the details of your new Cash Out account. If you requested this change,
                  and you are happy that everything is correct then please click &quot;confirm&quot; so that you can use this
                  cash out address.
                </Typography>

                <Typography variant={'body1'} sx={{ mb: 3 }}>
                  If you did not request this change, or the details are not correct, then please click &quot;cancel&quot;
                  and then supply new Cash Out account details on the &quot;My Wallets&quot; tab.
                </Typography>

                <Box sx={{ display: 'flex' }} justifyContent={'space-between'}>
                  <Button variant={'contained'} type="button" onClick={handleVerifyDisbursementAccount} disabled={apiVerifyResult.isLoading}>
                    Confirm
                  </Button>

                  <Button variant={'outlined'} type="button" onClick={handleCancelVerifyDisbursementAccount} disabled={apiCancelVerifyResult.isLoading}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

        </Box>
      </Box>
    </>
  )
}

export default VerifyDisbursementAccountPage
